import React, { memo, useRef, useEffect, useMemo, Fragment } from 'react';
import moment from 'moment';
import Checkbox from '@material-ui/core/Checkbox';
import { generatePDF } from '@abhisek507/html2multipagepdf';
import {
  Letter,
  Quality as ImageQuality,
  RegularPageSelector as pageSelector,
  PageElementSelector as pageElementSelector,
  PageWithMaxPossibleWidthSelector as pageWithMaxPossibleWidthSelector,
} from '@abhisek507/html2multipagepdf/constants';
import Button from '../../subComponents/button';
import Input from '../../subComponents/input';
import DateWrapper from '../../subComponents/date-wrapper';
import { removeXSSAttacks } from '../../../common/helper';
import { preparePDF } from '../../../utils/referralForm';
import {
  DateFormat,
  MaxDate,
  CheckBoxChecked,
  HypoglycemiaUnawarenessSymptoms,
  InputInjectInhaleIdsAnswerMap,
  PID_ProvidersAndCreatePDF,
  QID_PumpCheckbox,
  QID_InjectInhaleCheckboxes,
  QID_InjectInhaleInputs,
  QID_HbA1CInput,
  QID_HbA1CDate,
  QID_HypoglycemiaUnawarenessSymptomsCheckboxes,
  QID_CGM,
  QID_FrequentAdjustmentsInTreatment,
  DEFAULT_FORMFIELD,
  CMN_DATA,
  DEFAULT_PDF_INFO,
  QID_SMBGInput,
  QID_CGMInput,
} from '../../../constants/cmnTemplateForEversense.constant';
import { FEATURE } from '../../../utils/feature';
import AscensiaLogo from '../../../assets/ascensiaLogo.png';
import EversenseLogo from '../../../assets/eversenseLogoV2.svg';
import './styles.css';
// eslint-disable-next-line no-unused-vars
import { CMN_PDF_DATA } from '../../../constants/cmnTemplateForDexcom.constant';

const signatureInputWrapper = 'signature-input-wrapper';
const signatureDisplayWrapper = 'signature-display-wrapper';

export const CMNEversenseTemplateIdentifier = 'cmn-template-for-eversense';

const shiftDots = (text = '') => {
  if (!text.length) return text;

  const textBreakdown = text.split('');
  if (textBreakdown[0] !== '.') return text;

  textBreakdown.shift();
  return shiftDots(textBreakdown.join(''));
};

const popDots = (text = '') => {
  if (!text.length) return text;

  const textBreakdown = text.split('');
  if (textBreakdown[textBreakdown.length - 1] !== '.') return text;

  textBreakdown.pop();
  return popDots(textBreakdown.join(''));
};

export const trimDots = (text = '') => {
  return shiftDots(popDots(text));
};

export const extractNumber = (qAndA = { AnswerText: '' }) => {
  try {
    const regex = /[^0-9.\s]/gm;
    const text = (qAndA.AnswerText || '').replace(regex, '');
    const trimmedText = trimDots(text.trim());
    const hasNumber = !isNaN(trimmedText);
    const answer = hasNumber ? Number(trimmedText) : 0;
    return { hasNumber, answer };
  } catch (e) {
    return { hasNumber: false, answer: 0 };
  }
};

export const evaluateAnswerFromHbA1CInput = (qAndA = { AnswerText: '' }) => {
  try {
    const { answer } = extractNumber(qAndA);
    return { hasAnswer: (answer >= 1 && answer <= 25), answer };
  } catch(e) {
    return { hasAnswer: false, answer: 0 };
  }
};

const evaluateAnswerFromSMBGInput = (qAndA = { AnswerText: '' }) => {
  const expressions = [
    {
      regex: /(0 time)/gmi,
      response: { hasAnswer: true, answer: '0' },
    },
    {
      regex: /(1 time)/gmi,
      response: { hasAnswer: true, answer: '1' },
    },
    {
      regex: /(2 time)/gmi,
      response: { hasAnswer: true, answer: '2' },
    },
    {
      regex: /(3 time)/gmi,
      response: { hasAnswer: true, answer: '3' },
    },
    {
      regex: /(4 time)/gmi,
      response: { hasAnswer: true, answer: '4' },
    },
    {
      regex: /(more than 5 time)/gmi,
      response: { hasAnswer: true, answer: '5+' },
    },
    {
      regex: /(5 time)/gmi,
      response: { hasAnswer: true, answer: '5' },
    },
  ];

  for (let i = 0; i < expressions.length; i++) {
    const { regex, response } = expressions[i];
    if (regex.test(qAndA.AnswerText)) {
      return response;
    }
  }

  const { hasNumber, answer } = extractNumber(qAndA);
  if (!hasNumber) {
    return { hasAnswer: false, answer: '' };
  }

  if (answer > 5) {
    return { hasAnswer: true, answer: '5+' };
  }

  return { hasAnswer: true, answer: `${answer}` };
};

const evaluateAnswerFromCGMInput = (qAndA = { AnswerText: '' }) => {
  if (qAndA?.AnswerText?.length) {
    return { hasAnswer: true, answer: qAndA.AnswerText };
  }

  return { hasAnswer: false, answer: 'Unknown' };
};

const hasFeature = FEATURE();

export const SectionLabel = memo(({
  display = '',
}) => (
  <label
    className='section-label flex align-items-center justify-content-center p-l-4 p-r-4'
  >
    {display}
  </label>
));

export const SignatureWrapper = memo(({
  name,
  label,
  value,
  inputClassName = 'flex flex-fill m-r-4',
  onChange: handleChange = () => {}
}) => {

  return (
    <>
      <div className={`flex flex-fill align-items-center ${signatureInputWrapper}`}>
        <Input
          name={name}
          label={label}
          className={inputClassName}
          value={value}
          onChange={handleChange}
        />
      </div>
      <div className={`flex flex-fill align-items-center ${signatureDisplayWrapper} d-none`}>
        {
          label && <label className='input-label m-r-4'>{label}</label>
        }
        <span className={`signature-input-display-wrapper italic font-26 align-items-center ${inputClassName}`}>{value}</span>
      </div>
    </>
  );
});

const Order = memo(({
  value: {
    ...value
  },
  onChange: handleChange = () => {},
}) => (
  <div className='section order m-t-8'>
    <SectionLabel display='ORDER : Implantable Glucose Monitor System' />

    {
      CMN_DATA.orderInfo.questions.map(({ questionId, label }, index) => (
        <div key={questionId} className={`flex mn-col align-items-center ${index !== 0 ? 'border-top-primary' : ''}`}>
          <div className='flex min-width-110 p-l-2'>
            <Checkbox
              title={`checkbox-${questionId}`}
              className='checkbox'
              checked={value[`checkbox-${questionId}`]}
              // onChange={(_, _value) => { handleChange(`checkbox-${questionId}`, { target: { value: _value } }); }}
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <span className='flex align-items-center bold m-r-1'>{questionId}</span>
            <span className='flex align-items-center m-r-1'>-</span>
          </div>
          <div className='flex flex-fill'>
            <div className='flex flex-fill align-items-center p-2'>{label}</div>
          </div>
        </div>
      ))
    }
  </div>
));

const PatientInformation = memo(({
  value: {
    patientDob,
    patientFullName,
    patientAddress,
    patientState,
    patientZip,
    patientCity,
    patientPhone,
    patientInsuranceCompany,
    patientInsuranceNo,
  },
  onChange: handleChange = () => {}
}) => (
  <div className={`section patient-information m-t-8 p-1`}>
    <SectionLabel display='PATIENT INFORMATION' />

    <div className='flex flex-column gap-4'>
      <div className='flex align-items-center'>
        <Input name='patientFullName' label='Patient Last, First Name:' className='flex flex-fill m-r-4' value={patientFullName} onChange={handleChange.bind(this, 'patientFullName')} />
        <DateWrapper name='patientDob' label='Date of Birth:' value={patientDob} onChange={handleChange.bind(this, 'patientDob')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='patientAddress' label='Patient Address:' className='flex flex-fill m-r-4' value={patientAddress} onChange={handleChange.bind(this, 'patientAddress')} />
        <Input name='patientState' label='State:' className='m-r-4' value={patientState} onChange={handleChange.bind(this, 'patientState')} />
        <Input name='patientZip' label='Zip:' value={patientZip} onChange={handleChange.bind(this, 'patientZip')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='patientCity' label='City:' className='flex flex-fill m-r-4' value={patientCity} onChange={handleChange.bind(this, 'patientCity')} />
        <Input name='patientPhone' label='Phone:' value={patientPhone} onChange={handleChange.bind(this, 'patientPhone')} />
      </div>

      <div className='flex align-items-center d-none'>
        <Input name='patientInsuranceCompany' label='Insurance Company:' className='flex flex-fill m-r-4' value={patientInsuranceCompany} onChange={handleChange.bind(this, 'patientInsuranceCompany')} />
        <Input name='patientInsuranceNo' label='Insurance #:' value={patientInsuranceNo} onChange={handleChange.bind(this, 'patientInsuranceNo')} />
      </div>
    </div>
  </div>
));

const PrescriberInformation = memo(({
  value: {
    prescriberFullName,
    prescriberNpi,
    prescriberAddress,
    prescriberState,
    prescriberZip,
    prescriberCity,
    prescriberPhone,
    prescriberFax,
  },
  onChange: handleChange = () => {}
}) => (
  <div className={`section prescriber-information m-t-8 p-1`}>
    <SectionLabel display='Ordering Healthcare Provider (HCP)' />

    <div className='flex flex-column gap-4'>
      <div className='flex align-items-center'>
        <Input name='prescriberFullName' label='HCP Last, First Name:' className='flex flex-fill m-r-4'  value={prescriberFullName} onChange={handleChange.bind(this, 'prescriberFullName')} />
        <Input name='prescriberNpi' label='NPI #:' value={prescriberNpi} onChange={handleChange.bind(this, 'prescriberNpi')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='prescriberAddress' label='HCP Address:' className='flex flex-fill m-r-4' value={prescriberAddress} />
        <Input name='prescriberState' label='State:' className='m-r-4' value={prescriberState} />
        <Input type='text' name='prescriberZip' label='Zip:' value={prescriberZip} />
      </div>

      <div className='flex align-items-center'>
        <Input name='prescriberCity' label='City:' className='flex flex-fill m-r-4' value={prescriberCity} />
        <Input name='prescriberPhone' label='Phone:' className='m-r-4' value={prescriberPhone} />
        <Input name='prescriberFax' label='Fax:' value={prescriberFax} />
      </div>
    </div>
  </div>
));

const MedicalNecessity = memo(({
  value: {
    icd10Diagnosis,
    hbA1c,
    hbA1cDate,
    smbg,
    cgm,
    injectPerDay,
    inhalePerDay,
    lastVisitDate,
    followUpRoutine,

    ...value
  },
  onChange: handleChange = () => {},
}) => (
  <div className={`section medical-necessity m-t-8`}>
    <SectionLabel display='MEDICAL NECESSITY' />

    <div className='flex flex-column'>
      <div className='flex flex-fill font-12 p-1 bg-light-primary'>
        All shaded areas need to be completed by the HCP to document patient's need for I-CGM and chart/EMR requirements. Patients who do not meet their insurance plan's medical coverage criteria will be asked to sign a notice of their financial responsibility in advance of the procedure. E.g. Medicare ABN
      </div>

      <div className='flex p-1'>
        <div className='flex flex-column flex-fill'>
          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>
              <span className='bold m-r-1'>Examples of Diabetes Mellitus</span><span className='italic'>(not all inclusive)*:</span>
            </div>
            <div className='flex align-items-center justify-content-center align-center p-r-2 p-l-2 mn-col col-2'><span className='bold'>Underlying Conditions</span></div>
            <div className='flex align-items-center justify-content-center align-center p-r-2 p-l-2 mn-col col-3'><span className='bold'>T1</span></div>
            <div className='flex align-items-center justify-content-center align-center p-r-2 p-l-2 mn-col col-4'><span className='bold'>T2</span></div>
            <div className='flex align-items-center justify-content-center align-center p-r-2 p-l-2 mn-col col-5'><span className='bold'>Other Specified DM</span></div>
          </div>

          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>Hypoglycemia without coma</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-2 mn-col-border'>E08.649</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-3 mn-col-border'>E10.649</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-4 mn-col-border'>E11.649</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-5 mn-col-border'>E13.649</div>
          </div>

          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>Hyperglycemia</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-2 mn-col-border'>E08.65</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-3 mn-col-border'>E10.65</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-4 mn-col-border'>E11.65</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-5 mn-col-border'>E13.65</div>
          </div>

          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>Other specified complications</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-2 mn-col-border'>E08.69</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-3 mn-col-border'>E10.69</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-4 mn-col-border'>E11.69</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-5 mn-col-border'>E13.69</div>
          </div>

          <div className='flex'>
            <div className='flex align-items-center p-r-1 mn-col col-1'>Without complications</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-2 mn-col-border mn-col-border-bottom'>E08.9</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-3 mn-col-border mn-col-border-bottom'>E10.9</div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-4 mn-col-border mn-col-border-bottom'></div>
            <div className='flex align-items-center p-r-2 p-l-2 mn-col col-5 mn-col-border mn-col-border-bottom'>E13.9</div>
          </div>

          <div className='flex m-t-1'>
            <div className='flex col-1 align-items-center m-r-1'>
              <span className='input-label m-r-1'>Diagnosis ICD-10-CM</span>
              <span className='italic font-14 m-r-1'>(fill in):</span>
              <span className='required font-24'>*</span>
            </div>
            <div className='flex flex-fill col-2 align-items-center'>
              <Input name='icd10Diagnosis' label='' className='flex flex-fill' value={icd10Diagnosis} onChange={handleChange.bind(this, 'icd10Diagnosis')} />
            </div>
          </div>
        </div>
      </div>

      <div className='flex mn-col border-top-primary'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>Select Insulin Administration Type:</div>
        </div>
        <div className='flex'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 min-width-110'>
            <Checkbox
              title="checkbox-iat-pump"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-iat-pump']}
              onChange={(_, value) => { handleChange('checkbox-iat-pump', { target: { value } }); }}
            />
            <label>Pump</label>
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1'>
            <Checkbox
              title="checkbox-iat-inject"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-iat-inject']}
              onChange={(_, value) => { handleChange('checkbox-iat-inject', { target: { value } }); }}
            />
            <label className='m-r-2'>Inject</label>
            <Input name='injectPerDay' label='#' value={injectPerDay} inputClassName='width-100' onChange={handleChange.bind(this, 'injectPerDay')} />
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1'>
            <Checkbox
              title="checkbox-iat-inhale"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-iat-inhale']}
              onChange={(_, value) => { handleChange('checkbox-iat-inhale', { target: { value } }); }}
            />
            <label className='m-r-2'>Inhale</label>
            <Input name='inhalePerDay' label='#' value={inhalePerDay} inputClassName='width-100' onChange={handleChange.bind(this, 'inhalePerDay')} />
          </div>
        </div>
      </div>

      <div className='flex mn-col border-top-primary d-none'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>Number of Insulin Administration per day:</div>
        </div>
        <div className='flex'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1'>
            <Input name='injectPerDay' label='Inject:' value={injectPerDay} onChange={handleChange.bind(this, 'injectPerDay')} />
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1'>
            <Input name='inhalePerDay' label='Inhale:' value={inhalePerDay} onChange={handleChange.bind(this, 'inhalePerDay')} />
          </div>
        </div>
      </div>

      <div className='flex justify-content-flex-end mn-col border-top-primary'>
        <div className='flex justify-content-flex-end min-width-690'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1'>
            <Input name='hbA1c' label='HbA1c Value:' value={hbA1c} inputClassName='width-100' onChange={handleChange.bind(this, 'hbA1c')} />
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1'>
            <DateWrapper name='hbA1cDate' label='Date of HbA1c:' value={hbA1cDate} inputClassName='width-140' onChange={handleChange.bind(this, 'hbA1cDate')} />
          </div>
          {
            // This is a temporary feature flag. We must remove the temporary flag as soon as the change is Ready for Release.
            hasFeature.EVERSENSE_TEMPLATE_UPDATE && (
              <div className='flex align-items-center border-left-primary p-r-2 p-l-2'>
                <Input name='smbg' label='SMBG/Day #:' value={smbg} inputClassName='width-100' onChange={handleChange.bind(this, 'smbg')} />
              </div>
            )
          }
        </div>
      </div>

      {
        // This is a temporary feature flag. We must remove the temporary flag as soon as the change is Ready for Release.
        hasFeature.EVERSENSE_TEMPLATE_UPDATE && (
          <div className='flex justify-content-flex-end mn-col border-top-primary'>
            <div className='flex align-items-center border-left-primary p-r-1 p-l-1 min-width-520'>
              <Input
                name='cgm'
                label='CGM:'
                value={cgm}
                className='flex-fill'
                inputClassName='flex flex-fill'
                onChange={handleChange.bind(this, 'cgm')}
              />
            </div>
          </div>
        )
      }

      <div className='flex mn-col border-top-primary'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>
            <span className='m-r-1'>History of hypoglycemia unawareness or recurring episodes of severe hypoglycemia:</span>
            <span className='required font-24'>*</span>
          </div>
        </div>
        <div className='flex min-width-180'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-hhu-yes"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-hhu'] === 'yes'}
              onChange={(_, _value) => { handleChange('checkbox-hhu', { target: { value: _value ? 'yes' : '' } }); }}
            />
            <label>Yes</label>
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-hhu-no"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-hhu'] === 'no'}
              onChange={(_, _value) => { handleChange('checkbox-hhu', { target: { value: _value ? 'no' : '' } }); }}
            />
            <label>No</label>
          </div>
        </div>
      </div>

      <div className='flex mn-col border-top-primary'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>
            <span className='m-r-1'>Patient previously met CGM requirements by insurer but now elects I-CGM</span>
            <span className='required font-24'>*</span>
          </div>
        </div>
        <div className='flex min-width-180'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-icgm-yes"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-icgm'] === 'yes'}
              onChange={(_, _value) => { handleChange('checkbox-icgm', { target: { value: _value ? 'yes' : '' } }); }}
            />
            <label>Yes</label>
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-icgm-no"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-icgm'] === 'no'}
              onChange={(_, _value) => { handleChange('checkbox-icgm', { target: { value: _value ? 'no' : '' } }); }}
            />
            <label>No</label>
          </div>
        </div>
      </div>

      <div className='flex mn-col border-top-primary'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>
            <span className='m-r-1'>Patient requires long-term I-CGM more than 72 hours to 1 week for diagnostic use:</span>
            <span className='required font-24'>*</span>
          </div>
        </div>
        <div className='flex min-width-180'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-lticgm-yes"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-lticgm'] === 'yes'}
              onChange={(_, _value) => { handleChange('checkbox-lticgm', { target: { value: _value ? 'yes' : '' } }); }}
            />
            <label>Yes</label>
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-lticgm-no"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-lticgm'] === 'no'}
              onChange={(_, _value) => { handleChange('checkbox-lticgm', { target: { value: _value ? 'no' : '' } }); }}
            />
            <label>No</label>
          </div>
        </div>
      </div>

      <div className='flex mn-col border-top-primary'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>
            <span className='m-r-1'>Are frequent adjustments to insulin treatment required due to glucose monitor test results?</span>
            <span className='required font-24'>*</span>
          </div>
        </div>
        <div className='flex min-width-180'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-faitr-yes"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-faitr'] === 'yes'}
              onChange={(_, _value) => { handleChange('checkbox-faitr', { target: { value: _value ? 'yes' : '' } }); }}
            />
            <label>Yes</label>
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-faitr-no"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-faitr'] === 'no'}
              onChange={(_, _value) => { handleChange('checkbox-faitr', { target: { value: _value ? 'no' : '' } }); }}
            />
            <label>No</label>
          </div>
        </div>
      </div>

      <div className='flex border-top-primary'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>
            <span className='m-r-1'>Patient demonstrates an understanding of technology, is capable of using the device to recognize alerts and alarms, is motivated to use the device correctly and consistently, is willing to commit to clinical visits as needed for sensor replacement, and is expected to adhere to comprehensive diabetes treatment plan.</span>
            <span className='required font-24'>*</span>
          </div>
        </div>
        <div className='flex min-width-180'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-moti-yes"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-moti'] === 'yes'}
              onChange={(_, _value) => { handleChange('checkbox-moti', { target: { value: _value ? 'yes' : '' } }); }}
            />
            <label>Yes</label>
          </div>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1 width-90'>
            <Checkbox
              title="checkbox-moti-no"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={value['checkbox-moti'] === 'no'}
              onChange={(_, _value) => { handleChange('checkbox-moti', { target: { value: _value ? 'no' : '' } }); }}
            />
            <label>No</label>
          </div>
        </div>
      </div>

      <div className='flex mn-col border-top-primary'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>
            <span className='m-r-1'>What was last date of in-person visit with ordering healthcare provider</span>
            <span className='italic'>(within last 6 months):</span>
          </div>
        </div>
        <div className='flex'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1'>
            <DateWrapper name='lastVisitDate' label='Date:' value={lastVisitDate} onChange={handleChange.bind(this, 'lastVisitDate')} />
          </div>
        </div>
      </div>

      <div className='flex mn-col border-top-primary'>
        <div className='flex flex-fill'>
          <div className='flex flex-fill align-items-center justify-content-flex-end p-1'>
            <span className='m-r-1'>Routine follow-up care is expected within</span>
            <span className='italic'>(fill in date, number of weeks, number of month(s), etc.):</span>
          </div>
        </div>
        <div className='flex'>
          <div className='flex align-items-center border-left-primary p-r-1 p-l-1'>
            <Input name='followUpRoutine' label='' value={followUpRoutine} onChange={handleChange.bind(this, 'followUpRoutine')} />
          </div>
        </div>
      </div>
    </div>
  </div>
));

const AuthorizationReview = memo(({
  value: {
    urgentReview,

    ...value
  },
  onChange: handleChange = () => {}
}) => (
  <div className={`section prior-authorization m-t-8`}>
    <SectionLabel display='PRIOR AUTHORIZATION REVIEW' />

    <div className={`flex mn-col align-items-center`}>
      <div className='flex flex-column max-width-220'>
        <div className='flex'>
          <Checkbox
            title="checkbox-ur"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={value['checkbox-ur']}
            onChange={(_, value) => { handleChange('checkbox-ur', { target: { value } }); }}
          />
          <span className='flex align-items-center bold m-r-4'>Urgent Review</span>
        </div>

        <div className='flex'>
          <span className='flex font-12 italic p-l-1 p-b-1 p-r-1'>If checked, must include rationale to justify patient need for urgent request</span>
        </div>
      </div>
      <div className='flex flex-fill'>
        <Input name='urgentReview' label='' className='flex flex-fill p-t-1 p-b-1 p-r-1' value={urgentReview} onChange={handleChange.bind(this, 'urgentReview')} />
      </div>
    </div>
  </div>
));

const ProviderAttestation = memo(() => (
  <div className={`section provider-attestation m-t-8 p-1`}>
    <SectionLabel display='HEALTHCARE PROVIDER ATTESTATION' />

    <div className='flex align-items-flex-start'>
      This document serves as an Order and Statement of Medical Necessity for the above referenced patient for a THERAPEUTIC IMPLANTABLE CONTINUOUS GLUCOSE MONITORING SYSTEM (I-CGM); Sensor, Smart Transmitter, all associated Eversense E3 system components and all associated diabetes supplies to be provided by an authorized distributor. I certify that I am the healthcare provider identified in the above section and I certify that the medical necessity information contained in this document is true, accurate and complete, to the best of my knowledge.
    </div>
  </div>
));

const ProviderSignature = memo(({
  value: {
    prescriberPreName,
    hcpSignature,
    signatureDate,
  },
  onChange: handleChange = () => {}
}) => (
  <div className={`section provider-signature m-t-8 p-1`}>
    <SectionLabel display='HEALTHCARE PROVIDER SIGNATURE' />

    <div className='flex align-items-center'>
      <SignatureWrapper
        name='hcpSignature'
        label='Electronically approved by:'
        value={hcpSignature}
        onChange={handleChange.bind(this, 'hcpSignature')}
      />
      <DateWrapper name='signatureDate' label='Date:' value={signatureDate} max={MaxDate} />
    </div>
  </div>
));

export const SOAPNotes = memo(({
  notes,
}) => {
  const { soapNotes } = useMemo(() => {
    try {
      const soapNotes = notes.medicalOrderNotes.OrderNotes.filter(({
        Text
      }) => {
        const regex = /(SOAP NOTE)/g;
        return regex.test(Text);
      });
      return { soapNotes };
    } catch(e) {
      return { soapNotes: [] };
    }
  }, [notes]);

  return (
    <div className='section provider-signature m-t-8'>
      <SectionLabel display='SOAP NOTES' />
      {
        soapNotes.map(({ EntryDate: time, Text: text }, index) => (
          <div className={`flex flex-column gap-8 mn-col align-items-flex-start p-2 ${pageElementSelector} ${(index !== 0) ? 'border-top-primary' : ''}`}>
            <div className='bold'>{time}</div>
            <div dangerouslySetInnerHTML={{ __html: removeXSSAttacks(text) }}></div>
          </div>
        ))
      }
    </div>
  );
});

export const QuestionAnswers = memo(({
  value = {},
  qAndAs = [],
  className = '',
  heading: Heading = Fragment,
  topContent: TopContent = Fragment,
  footer: Footer = Fragment,
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => {
  return qAndAs.map(({ ProductId: pId, ProductTitle: title, QuestionAnswers: qAndAs = [] }) => (
    <div className={`page border-primary flex flex-column gap-20 max-width-1024 ${pageWithMaxPossibleWidthSelector} ${className} d-none`}>
      <Heading value={value} onChange={handleChange} />

      <div className='content flex flex-column flex-fill'>
        <TopContent value={value} onChange={handleChange} />

        <div key={pId} className='section provider-signature m-t-8 p-4'>
          <SectionLabel display={`MEDICAL QUESTIONNAIRE ANSWERED BY THE PATIENT FOR PRODUCT - ${title} - (${pId})`} />

          <div className='flex flex-column gap-16'>
            {
              qAndAs.map(({ QuestionId: qId, QuestionText: question, AnswerText: answer }, index = 0, questions = []) => (
                <div key={`${pId}-${qId}`} className={`flex flex-column gap-4 mn-col justify-content-center ${pageElementSelector}`}>
                  <span className='flex align-items-center bold line-20 font-16 font-roboto'>
                    Q. {question}
                  </span>
                  <span className='flex align-items-center line-20 font-16 font-roboto'>
                    Ans. {answer}
                  </span>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <Footer onSubmit={handleSubmit} />
    </div>
  ));
});

const Heading = memo(({
  value: {
    sentTo,
    sentDate,
  },
}) => (
  <div className='heading flex m-b-1'>

    <div className='flex min-width-165 heading-logo'>
      <div className='flex align-items-center justify-content-center'>
        <img src={EversenseLogo} alt='eversense_logo' className='min-width-165' />
      </div>
    </div>

    <div className='flex flex-column min-width-40 align-items-center'>
      <span className='flex flex-fill border-left-dark-grey'></span>
    </div>

    <div className='flex flex-fill'>
      <div className='flex align-items-center min-width-55 m-r-5'>
        <img src={AscensiaLogo} alt='ascensia_logo' className='height-50' />
      </div>

      <div className='flex flex-column flex-fill'>
        <div className='flex align-items-center m-b-2'>
          <span className='bold letter-spacing-1'>ORDER FOR: THERAPEUTIC IMPLANTABLE CONTINUOUS GLUCOSE MONITORING</span>
        </div>

        <div className='flex align-items-center m-b-2'>
          <Input name='sentTo' label='SENT TO:' className='flex flex-fill m-r-4' value={sentTo} />
          <DateWrapper name='sentDate' label='DATE:' value={sentDate} max={MaxDate} />
        </div>

        <div className='flex justify-content-center'>
          <span className='font-16 italic'>Order to be completed by Healthcare Provider in its Entirety</span>
        </div>
      </div>
    </div>

  </div>
));

const Footer = memo(({
  onSubmit: handleSubmit = () => {}
}) => (
  <div className='footer flex flex-column'>
    <p className='font-12 m-0'>
      * The medical necessity criteria used in this form was based on CMS LCDs for Implantable Continuous Glucose Monitoring (accessed 06/14/23). Other insurer requirements may vary by plan.
    </p>
    <div className='flex justify-content-flex-end m-t-4 d-none'>
      <Button
        variant='contained'
        color='primary'
        onClick={handleSubmit}
        label='Download Template'
      />
    </div>
  </div>
));

const SectionA = memo(({
  value = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div className={`page border-primary ${pageSelector} ${className}`}>
    <Heading value={value} onChange={handleChange} />
    <div className={`content flex flex-column gap-4 ${pageElementSelector}`}>
      <Order value={value} onChange={handleChange} />
      <PatientInformation value={value} onChange={handleChange} />
      <PrescriberInformation value={value} onChange={handleChange} />
      <MedicalNecessity value={value} onChange={handleChange} />
      <AuthorizationReview value={value} onChange={handleChange} />
      <ProviderAttestation value={value} onChange={handleChange} />
      <ProviderSignature value={value} onChange={handleChange} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
));

const SectionB = memo(({
  value = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  className = '',
  heading: Heading = Fragment,
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => {
  const { qAndAs } = useMemo(() => {
    try {
      const productIdsForPDFInSettings = productInfo.StoreFrontData.StorefrontApplicationSettings.get(PID_ProvidersAndCreatePDF);
      const productIds = productIdsForPDFInSettings.value.split(',').map((pId) => Number(pId));
      const allQAndAs = consultInfo.ProductConsultDetails;
      const qAndAs = allQAndAs.filter(({ ProductId }) => productIds.includes(ProductId));
      return { productIds, qAndAs };
    } catch(e) {
      return { productIds: [], qAndAs: [] };
    }
  }, [productInfo, consultInfo]);

  return (
    <QuestionAnswers
      value={value}
      qAndAs={qAndAs}
      productInfo={productInfo}
      patientInfo={patientInfo}
      consultInfo={consultInfo}
      className={className}
      heading={Heading}
      footer={Footer}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
});

const SectionC = memo(({
  value = {},
  notes = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div className={`page border-primary flex flex-column max-width-1024 ${pageWithMaxPossibleWidthSelector} ${className} d-none`}>
    <Heading value={value} onChange={handleChange} />
    <div className='content flex flex-column flex-fill m-b-5'>
      <SOAPNotes notes={notes} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
));

export const CMNTemplateForEversense = ({
  id='',
  value = {},
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  reference,
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => {
  return (
    <div ref={reference} id={id} className={CMNEversenseTemplateIdentifier}>
      <SectionA
        value={value}
        className={className}
        onSubmit={handleSubmit}
        onChange={handleChange}
      />

      <SectionC
        value={value}
        notes={notes}
        className={className}
        onSubmit={handleSubmit}
        onChange={handleChange}
      />

      <SectionB
        value={value}
        productInfo={productInfo}
        patientInfo={patientInfo}
        consultInfo={consultInfo}
        className={className}
        heading={Heading}
        onSubmit={handleSubmit}
        onChange={handleChange}
      />
    </div>
  )
};

export const TemplateDownloadManager = ({
  id = '',
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  formField = { ...DEFAULT_FORMFIELD },

  getCmnPdfData = async () => {},
  setFormField = () => {}
}) => {
  const reference = useRef();

  /**
   * @description create canvas of the template & print in a pdf file
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    preparePDF(
      CMNEversenseTemplateIdentifier,
      [pageSelector, pageWithMaxPossibleWidthSelector],
      Letter,
      pageElementSelector,
      ImageQuality[100],
      generatePDF
    ).then(() => {
      // code snippet if required
    });
  };

  const handleChange = (name, event) => {
    setFormField({ ...formField, [name]: event.target.value });
  };

  const loadData = async () => {
    const response  = await getCmnPdfData({
      OrderId: productInfo.OrderId,
      PaitentId: patientInfo.UserId,
      template: 'Eversense'
    });

    const pdfData = (() => {
      try {
        return response.data.Data || DEFAULT_PDF_INFO();
        // return CMN_PDF_DATA;
      } catch(e) {
        return DEFAULT_PDF_INFO();
      }
    })();

    let nextFormField = {
      ...DEFAULT_FORMFIELD,

      patientDob: moment(pdfData.patientInfo.dob).format(DateFormat),
      patientFullName: `${pdfData.patientInfo.lastName}, ${pdfData.patientInfo.firstName}`,
      patientAddress: pdfData.patientInfo.address,
      patientState: pdfData.patientInfo.state,
      patientZip: pdfData.patientInfo.zip,
      patientCity: pdfData.patientInfo.city,
      patientPhone: pdfData.patientInfo.phone,

      prescriberPreName: pdfData.prescriberInfo.preName,
      prescriberFullName: `${pdfData.prescriberInfo.lastName}, ${pdfData.prescriberInfo.firstName}`,
      prescriberNpi: pdfData.prescriberInfo.npiNo,

      lastVisitDate: MaxDate,

      hcpSignature: `${pdfData.provider.signature.prifix} ${pdfData.provider.signature.firstName} ${pdfData.provider.signature.lastNmae}`,
      signatureDate: MaxDate,
    };

    const questionAnswersMap = new Map([[0, []]]);
    const productInfoWithQuestionAnswers = consultInfo.ProductConsultDetails.find(({ ProductId }) => ProductId === productInfo.pdfInfo.productId);
    if (productInfoWithQuestionAnswers && productInfoWithQuestionAnswers.QuestionAnswers) {
      for (let i = 0; i < productInfoWithQuestionAnswers.QuestionAnswers.length; i++) {
        const qAndA = productInfoWithQuestionAnswers.QuestionAnswers[i];
        let qAndAs = [qAndA];
        if (questionAnswersMap.has(qAndA.QuestionId)) {
          const _qAndAs = questionAnswersMap.get(qAndA.QuestionId);
          qAndAs = [..._qAndAs, qAndA];
        }
        questionAnswersMap.set(qAndA.QuestionId, qAndAs);
      }
    }

    const config = (() => {
      try {
        return new Map(productInfo.StoreFrontData.StorefrontApplicationSettings) || new Map([['', {}]]);
      } catch(e) {
        return new Map([['', {}]]);
      }
    })();

    console.log('@logger - initializing the pdf template', {
      config,
      pdfData,
      pdfInfo: productInfo.pdfInfo,
      questionAnswers: questionAnswersMap,
    });

    /**
     * @description The below conditional block determines whether to check the 'Pump' checkbox or not.
     * It depends on the below parameters.
     * Question - "Do you currently use an insulin pump?"
     * App settings - QID_PumpCheckbox
     * Possible App settings value - 109930 or 110266,110239 or 109532,109409,
     */
    if (config.has(QID_PumpCheckbox)) {
      const checkboxIatPumpIds = config.get(QID_PumpCheckbox).value.split(',');
      for (let i = 0; i < checkboxIatPumpIds.length; i++) {
        const qId = Number(checkboxIatPumpIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          if (qAndA.AnswerText.toLowerCase() === CheckBoxChecked.yes) {
            nextFormField['checkbox-iat-pump'] = true;
          }
        }

        if (nextFormField['checkbox-iat-pump']) {
          break;
        }
      }
    }

    /**
     * @description The below conditional block determines whether to check the 'Inject'/'Inhale' checkboxes or not.
     * It depends on the below parameters.
     * Question - "What type(s) of insulin are you currently using? Select all that may apply."
     * App settings - QID_InjectInhaleCheckboxes
     * Possible App settings value - 109932 or 110267,110241 or 109534,109411,
     */
    if (config.has(QID_InjectInhaleCheckboxes)) {
      const checkboxIatInjectIds = config.get(QID_InjectInhaleCheckboxes).value.split(',');
      for (let i = 0; i < checkboxIatInjectIds.length; i++) {
        const qId = Number(checkboxIatInjectIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const regexInject = /(injectable)|(lantus)|(tresiba)|(levemir)|(toujeo)|(fiasp)|(humalog)|(novolog)|(humulin)|(novolin)/gi;
        const regexInhale = /(inhale)|(afrezza)/gi;
        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];

          if (regexInject.exec(qAndA.AnswerText) !== null) {
            nextFormField['checkbox-iat-inject'] = true;
          }

          if (regexInhale.exec(qAndA.AnswerText) !== null) {
            nextFormField['checkbox-iat-inhale'] = true;
          }
        }

        if (nextFormField['checkbox-iat-inject'] && nextFormField['checkbox-iat-inhale']) {
          break;
        }
      }
    }

    /**
     * @description The below conditional block determines the values of the Inject/Inhale inputboxes.
     * It depends on the below parameters.
     * Question - "Considering all insulin types, how many times per day are you currently taking insulin?"
     * App settings - QID_InjectInhaleInputs
     * Possible App settings value - 109929 or 110264,110238 or 109531,109408
     */
    if (config.has(QID_InjectInhaleInputs)) {
      const inputInjectIds = config.get(QID_InjectInhaleInputs).value.split(',');
      for (let i = 0; i < inputInjectIds.length; i++) {
        const qId = Number(inputInjectIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          const answer = InputInjectInhaleIdsAnswerMap.get(qAndA.AnswerText.toLowerCase());

          if (nextFormField['checkbox-iat-inject'] && answer) {
            nextFormField.injectPerDay = answer;
          }
          if (nextFormField['checkbox-iat-inhale'] && answer) {
            nextFormField.inhalePerDay = answer;
          }
        }
      }
    }

    /**
     * @description The below conditional block determines the value of the HbA1c inputbox.
     * It depends on the below parameters.
     * Question - "What was our latest HbA1C result?"
     * App settings - QID_HbA1CInput
     * Possible App settings value - 109916 or 110254,110225 or 109518,109395,109386
     */
    if (config.has(QID_HbA1CInput)) {
      const inputHbA1cIds = config.get(QID_HbA1CInput).value.split(',');
      for (let i = 0; i < inputHbA1cIds.length; i++) {
        const qId = Number(inputHbA1cIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = evaluateAnswerFromHbA1CInput(qAndAs[j]);
          nextFormField.hbA1c = hasAnswer ? answer : 'Unknown';

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField.hbA1c !== 'Unknown') {
          break;
        }
      }
    }

    /**
     * @description The below conditional block determines the value of the Date of HbA1c inputbox.
     * It depends on the below parameters.
     * Question - "When was your last HbA1C taken?"
     * App settings - QID_HbA1CDate
     * Possible App settings value - 109917 or 110255,110226 or 109519,109396,109385
     */
    if (config.has(QID_HbA1CDate)) {
      const inputHbA1cDateIds = config.get(QID_HbA1CDate).value.split(',');
      for (let i = 0; i < inputHbA1cDateIds.length; i++) {
        const qId = Number(inputHbA1cDateIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];

          if (qAndA.AnswerText) {
            nextFormField.hbA1cDate = moment(qAndA.AnswerText).format(DateFormat);
          }
        }

        if (nextFormField.hbA1cDate) {
          break;
        }
      }
    }

    const Evaluations = new Map([
      [QID_SMBGInput, {
        key: QID_SMBGInput,
        fn: evaluateAnswerFromSMBGInput,
        field: 'smbg',
      }],
      [QID_CGMInput, {
        key: QID_CGMInput,
        fn: evaluateAnswerFromCGMInput,
        field: 'cgm',
      }],
    ]);

    /**
     * @description This conditional block determines the value of the SMBG/Day inputbox.
     */
    (() => {
      const { key, fn, field } = Evaluations.get(QID_SMBGInput);
      
      if (!config.has(key)) {
        return;
      }

      const ids = config.get(key).value.split(',');
      for (let i = 0; i < ids.length; i++) {
        const qId = Number(ids[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = fn(qAndAs[j]);
          nextFormField[field] = hasAnswer ? answer : 'Unknown';

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField[field] !== 'Unknown') {
          break;
        }
      }
    })();

    /**
     * @description This conditional block determines the value of the SMBG/Day inputbox.
     */
    (() => {
      const { key, fn, field } = Evaluations.get(QID_CGMInput);
      
      if (!config.has(key)) {
        return;
      }

      const ids = config.get(key).value.split(',');
      for (let i = 0; i < ids.length; i++) {
        const qId = Number(ids[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const { hasAnswer, answer } = fn(qAndAs[j]);
          nextFormField[field] = answer;

          if (hasAnswer) {
            break;
          }
        }

        if (nextFormField[field] !== 'Unknown') {
          break;
        }
      }
    })();

    /**
     * @description The below conditional block determines the value of the checkbox for "History of hypoglycemia unawareness or recurring episodes of severe hypoglycemia:".
     * It depends on the below parameters.
     * Question - "Do any of the following apply to you?"
     * App settings - QID_HypoglycemiaUnawarenessSymptomsCheckboxes
     * Possible App settings value - 109915 or 110265,110224 or 109517,109394,
     */
    if (config.has(QID_HypoglycemiaUnawarenessSymptomsCheckboxes)) {
      const checkboxHhuIds = config.get(QID_HypoglycemiaUnawarenessSymptomsCheckboxes).value.split(',');
      for (let i = 0; i < checkboxHhuIds.length; i++) {
        const qId = Number(checkboxHhuIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          for (let k = 0; k < HypoglycemiaUnawarenessSymptoms.length; k++) {
            const hasHypoglycemia = qAndA.AnswerText.toLowerCase().includes(HypoglycemiaUnawarenessSymptoms[k].toLowerCase());
            if (hasHypoglycemia) {
              nextFormField['checkbox-hhu'] = CheckBoxChecked.yes;
              break;
            }
          }

          if (nextFormField['checkbox-hhu'] === CheckBoxChecked.yes) {
            break;
          }
        }

        if (nextFormField['checkbox-hhu'] === CheckBoxChecked.yes) {
          break;
        }
      }
    }

    /**
     * @description The below conditional block determines the value of the checkbox for "Patient previously met CGM requirements by insurer but now elects I-CGM (not a requirement for I-CGM):".
     * It depends on the below parameters.
     * Question - "Have you been prescribed and approved by your insurance for a Continuous Glucose Monitor (CGM)?"
     * App settings - QID_CGM
     * Possible App settings value - 110277,110252
     */
    if (config.has(QID_CGM)) {
      const checkboxIcgmIds = config.get(QID_CGM).value.split(',');
      for (let i = 0; i < checkboxIcgmIds.length; i++) {
        const qId = Number(checkboxIcgmIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          nextFormField['checkbox-icgm'] = CheckBoxChecked.no;

          if (qAndA.AnswerText.toLowerCase() === CheckBoxChecked.yes) {
            nextFormField['checkbox-icgm'] = CheckBoxChecked.yes;
            break;
          }
        }

        if (nextFormField['checkbox-icgm'] === CheckBoxChecked.yes) {
          break;
        }
      }
    }

    /**
     * @description The below conditional block determines the value of the checkbox for "Are frequent adjustments to insulin treatment required due to glucose monitor test results?".
     * It depends on the below parameters.
     * Question - "Do you use a sliding scale to determine your insulin doses at mealtime?"
     * App settings - QID_FrequentAdjustmentsInTreatment
     * Possible App settings value - 110262,110236
     */
    if (config.has(QID_FrequentAdjustmentsInTreatment)) {
      const checkboxFaitrIds = config.get(QID_FrequentAdjustmentsInTreatment).value.split(',');
      for (let i = 0; i < checkboxFaitrIds.length; i++) {
        const qId = Number(checkboxFaitrIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          if (qAndA.AnswerText.toLowerCase() === CheckBoxChecked.yes) {
            nextFormField['checkbox-faitr'] = CheckBoxChecked.yes;
            break;
          }
        }

        if (nextFormField['checkbox-faitr'] === CheckBoxChecked.yes) {
          break;
        }
      }
    }

    setFormField(nextFormField);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className='flex flex-column flex-fill'>
      <CMNTemplateForEversense
        id={id}
        value={formField}
        notes={notes}
        productInfo={productInfo}
        patientInfo={patientInfo}
        consultInfo={consultInfo}
        reference={reference}
        onSubmit={handleSubmit}
        onChange={handleChange}
        className='m-b-4 p-1'
      />
    </div>
  );
};

export default memo(TemplateDownloadManager);
